import type { ComponentProps, ElementType } from 'react';
import {
  Autocomplete,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type AutocompleteProps,
} from '@mui/material';

import {
  RECEPTION_FEATURES_LEVELS,
  RECEPTION_FEATURES_LEVELS_LABELS,
} from '@/components/nomenclature/modals/ReceptionFeatures/constants';
import type { ReceptionFeatureLevel } from '@/components/nomenclature/types';
import { ReactComponent as CloseSVG } from '@/shared/assets/icons-2.0/close.svg';
import { ReactComponent as DoneSVG } from '@/shared/assets/icons-2.0/done.svg';
import { ReactComponent as EmergencySVG } from '@/shared/assets/icons-2.0/emergency_home.svg';

// ----------------------------------------------------------------------

export type ReceptionFeaturesAutocompleteProps = Omit<
  AutocompleteProps<ReceptionFeatureLevel, false, false, false>,
  'options' | 'value' | 'renderInput'
> & {
  value: ReceptionFeatureLevel;
};

type ReceptionFeatureLevelItemProps = {
  level: ReceptionFeatureLevel;
};

// ----------------------------------------------------------------------

const iconsByReceptionFeatureLevel: Record<ReceptionFeatureLevel, ElementType<ComponentProps<'svg'>>> = {
  allowed: DoneSVG,
  forbidden: CloseSVG,
  with_caution: EmergencySVG,
};

const colorsByReceptionFeatureLevel: Record<ReceptionFeatureLevel, string> = {
  allowed: '#00A76B',
  forbidden: '#FF3000',
  with_caution: '#FD8900',
};

// ----------------------------------------------------------------------

const ReceptionFeatureLevelItem = (props: ReceptionFeatureLevelItemProps) => {
  const { level } = props;

  const IconSVG = iconsByReceptionFeatureLevel[level];
  const label = RECEPTION_FEATURES_LEVELS_LABELS[level];

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <IconSVG width={20} height={20} fill={colorsByReceptionFeatureLevel[level]} />
      <Typography variant="subtitle2">{label}</Typography>
    </Stack>
  );
};

// ----------------------------------------------------------------------

export const ReceptionFeaturesAutocomplete = (props: ReceptionFeaturesAutocompleteProps) => {
  const { value = 'allowed', ...restOfProps } = props;

  const IconSVG = iconsByReceptionFeatureLevel[value];

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Autocomplete
      {...restOfProps}
      options={RECEPTION_FEATURES_LEVELS}
      value={value}
      getOptionLabel={option => RECEPTION_FEATURES_LEVELS_LABELS[option]}
      renderOption={({ key, ...props }, level) => (
        <MenuItem key={key} value={level} {...props}>
          <ReceptionFeatureLevelItem level={level} />
        </MenuItem>
      )}
      renderInput={inputParams => (
        <TextField
          {...inputParams}
          variant="filled"
          sx={{
            caretColor: 'transparent',
            input: {
              cursor: 'pointer',
              userSelect: 'none',
            },
          }}
          placeholder="Выбрать"
          onKeyDown={handleOnKeyDown}
          InputProps={{
            ...inputParams.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                {value && <IconSVG fill={colorsByReceptionFeatureLevel[value]} />}
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
